import { z } from 'zod';
import { EMAIL_PATTERN, PHONE_PATTERN } from './catalog.js';
// TEAM: the employee directory, project membership and project roles. An employee exists once and
// may belong to several projects. An employee record never grants sign-in: that needs a verified
// identity binding of an active account (done outside this screen) and roles in a project.
export const EMPLOYEE_STATUSES = ['active', 'inactive'];
export const EMPLOYEE_STATUS_LABELS = {
    active: 'פעיל',
    inactive: 'לא פעיל',
};
/** The employee's sign-in account: none, pending (no verified identity, or not activated), active. */
export const ACCOUNT_STATES = ['none', 'pending', 'active', 'deactivated'];
export const ACCOUNT_STATE_LABELS = {
    none: 'אין חשבון כניסה',
    pending: 'חשבון ממתין לאימות זהות',
    active: 'חשבון כניסה פעיל',
    deactivated: 'חשבון כניסה מושבת',
};
export const TEAM_LIMITS = { fullName: 120, responsibility: 80, templates: 20 };
const optionalText = (max, pattern) => z
    .string()
    .trim()
    .max(max)
    .regex(pattern)
    .or(z.literal(''))
    .nullable()
    .transform((value) => (value === '' ? null : value));
export const roleRefSchema = z.object({ templateId: z.uuid(), name: z.string() }).strict();
export const accountSchema = z
    .object({ state: z.enum(ACCOUNT_STATES), identityVerified: z.boolean() })
    .strict();
export const INVITATION_STATES = ['pending', 'expired', 'revoked', 'accepted'];
export const INVITATION_STATE_LABELS = {
    pending: 'הזמנה ממתינה',
    expired: 'פג תוקף ההזמנה',
    revoked: 'ההזמנה בוטלה',
    accepted: 'ההזמנה נוצלה',
};
export const invitationStatusSchema = z
    .object({ state: z.enum(INVITATION_STATES), expiresAt: z.iso.datetime({ offset: true }) })
    .strict();
export const membershipSchema = z
    .object({
    projectId: z.uuid(),
    projectCode: z.string(),
    projectName: z.string(),
    responsibility: z.string(),
    since: z.iso.datetime({ offset: true }),
    roles: z.array(roleRefSchema),
})
    .strict();
export const employeeSchema = z
    .object({
    id: z.uuid(),
    fullName: z.string(),
    workEmail: z.string().nullable(),
    phone: z.string().nullable(),
    status: z.enum(EMPLOYEE_STATUSES),
    rowVersion: z.number().int().min(1),
    account: accountSchema,
    /** The latest sign-in invitation link (hosted deployment), without the link itself. */
    invitation: invitationStatusSchema.nullable().optional(),
    /** The signed-in user's own employee record (changes to it can remove the user's access). */
    isSelf: z.boolean(),
    memberships: z.array(membershipSchema),
    organizationRoles: z.array(roleRefSchema),
})
    .strict();
/**
 * `POST /employees/:id/invitation`: the one-time sign-in link, shown once to the Super Admin who
 * created it (never stored or logged), to copy and send privately.
 */
export const invitationResponseSchema = z
    .object({
    employee: employeeSchema,
    link: z.url({ protocol: /^https?$/ }),
    expiresAt: z.iso.datetime({ offset: true }),
})
    .strict();
/** A role template, with whether the caller may grant (and revoke) it in the scope at hand. */
export const templateOptionSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    permissions: z.array(z.string()),
    grantable: z.boolean(),
})
    .strict();
/** `GET /employees`: the organization's employees (organization-wide view). */
export const employeeDirectoryResponseSchema = z
    .object({
    items: z.array(employeeSchema),
    /** Templates for organization-wide roles (the business Super Admin). */
    templates: z.array(templateOptionSchema),
    permissions: z
        .object({
        manage: z.boolean(),
        /** Sign-in invitations can be sent (hosted deployment, access managers only). */
        invite: z.boolean().optional(),
    })
        .strict(),
})
    .strict();
export const employeeInputSchema = z
    .object({
    fullName: z.string().trim().min(2).max(TEAM_LIMITS.fullName),
    workEmail: optionalText(254, EMAIL_PATTERN),
    phone: optionalText(25, PHONE_PATTERN),
})
    .strict();
export const employeeUpdateSchema = employeeInputSchema
    .extend({ rowVersion: z.number().int().min(1), status: z.enum(EMPLOYEE_STATUSES) })
    .strict();
export const rolesRequestSchema = z
    .object({ templateIds: z.array(z.uuid()).max(TEAM_LIMITS.templates) })
    .strict();
/** A project member, as the project's team page shows them. */
export const teamMemberSchema = z
    .object({
    employee: z
        .object({
        id: z.uuid(),
        fullName: z.string(),
        workEmail: z.string().nullable(),
        phone: z.string().nullable(),
        status: z.enum(EMPLOYEE_STATUSES),
        account: accountSchema,
        /** The signed-in user (removing them, or their roles, removes the user's own access). */
        isSelf: z.boolean(),
    })
        .strict(),
    responsibility: z.string(),
    since: z.iso.datetime({ offset: true }),
    roles: z.array(roleRefSchema),
})
    .strict();
/** `GET /projects/:id/team`: the project's members, and what the caller may change. */
export const projectTeamResponseSchema = z
    .object({
    project: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).strict(),
    members: z.array(teamMemberSchema),
    templates: z.array(templateOptionSchema),
    /** Active employees who are not members (names only), for adding; empty without manage. */
    candidates: z.array(z.object({ id: z.uuid(), fullName: z.string() }).strict()),
    permissions: z.object({ manage: z.boolean() }).strict(),
})
    .strict();
export const membershipInputSchema = z
    .object({
    employeeId: z.uuid(),
    responsibility: z.string().trim().min(2).max(TEAM_LIMITS.responsibility),
})
    .strict();
/**
 * Refusals that keep the organization administrable (409 `conflict.code`, with the Hebrew text in
 * `conflict.details.message`): nobody removes their own access, and the organization always keeps
 * at least one active business administrator (`organization.access.manage`, DR-187).
 */
export const ACCESS_SAFETY_MESSAGES = {
    self_removal: 'לא ניתן להסיר את עצמך מצוות הפרויקט. פנו למנהל־על אחר.',
    self_revoke: 'לא ניתן להסיר הרשאות מעצמך. פנו למנהל אחר שיבצע את השינוי.',
    self_deactivation: 'לא ניתן לסמן את עצמך כעובד לא פעיל. פנו למנהל אחר.',
    last_admin: 'זהו מנהל הארגון הפעיל האחרון: לא ניתן להשבית אותו או להסיר ממנו את הרשאות הניהול. יש למנות מנהל נוסף קודם.',
    invite_no_email: 'לעובד אין כתובת דוא״ל עבודה. יש להוסיף כתובת לפני שליחת הזמנה.',
    invite_domain: 'ניתן להזמין רק כתובות דוא״ל של הארגון.',
    invite_inactive: 'לא ניתן להזמין עובד לא פעיל או חשבון מושבת.',
    invite_active: 'לעובד כבר יש חשבון כניסה פעיל.',
    invite_conflict: 'החשבון בשירות הזהות כבר מקושר לעובד אחר. פנו למנהל המערכת.',
    invite_none: 'אין לעובד זה הזמנה פתוחה לביטול.',
};
//# sourceMappingURL=team.js.map